// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@bg/XDL/types";
import { autoClamp } from "@bg/XDL/Utilities";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

export const bpFontConfig = {
    url: "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;700&display=swap",
    weight: {
        light: 300,
        regular: 400,
        semiBold: 500,
        bold: 700,
    },
    fontSize: {
        xxSmall: 12,
        xSmall: 13,
        small: 14,
        regular: 15,
        large: 18,
        legend: 10,
    },
    lineHeight: {
        xSmall: "21px",
        small: "22px",
        regular: "23px",
        singleLine: "21px",
        singleLineSmall: "16px",
        singleLinexSmall: "15px",
        singleLinexXSmall: "14px",
        legend: "12px",
    },
    spacing: {
        xSmall: "13px",
        small: "14px",
        regular: "15px",
        heading: "20px",
        smallHeading: "16px",
        xSmallHeading: "12px",
    },
    fontFamily: "Poppins, sans-serif",
};

export default {
    fontFamilyImportUrl: "",
    body: {
        size: "15px",
        lineHeight: "23px",
        weight: bpFontConfig.weight.regular,
        fontFamily: bpFontConfig.fontFamily,
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
        `,
    },
    p: {
        size: "15px",
        lineHeight: "23px",
        weight: bpFontConfig.weight.regular,
        fontFamily: bpFontConfig.fontFamily,
        css: css`
            margin-bottom: ${bpFontConfig.spacing.small};
            display: block;
        `,
    },
    h1: {
        size: autoClamp(34, 34, 40),
        lineHeight: autoClamp(42, 42, 48),
        weight: bpFontConfig.weight.bold,
        css: css`
            margin-bottom: ${bpFontConfig.spacing.heading};
            display: block;
        `,
    },
    h2: {
        size: autoClamp(28, 28, 32),
        lineHeight: autoClamp(36, 36, 40),
        weight: bpFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bpFontConfig.spacing.smallHeading};
            display: block;
        `,
    },
    h3: {
        size: autoClamp(22, 22, 26),
        lineHeight: autoClamp(31, 31, 34),
        weight: bpFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bpFontConfig.spacing.smallHeading};
            display: block;
        `,
    },
    h4: {
        size: 20,
        lineHeight: "28px",
        weight: bpFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bpFontConfig.spacing.xSmallHeading};
            display: block;
        `,
    },
    h5: {
        size: 18,
        lineHeight: "25px",
        weight: bpFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bpFontConfig.spacing.xSmallHeading};
            display: block;
        `,
    },
    h6: {
        size: 16,
        lineHeight: "23px",
        weight: bpFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bpFontConfig.spacing.xSmallHeading};
            display: block;
        `,
    },
    headerPrimary: {
        forwardAs: "p",
        size: "24px",
        fontFamily: bpFontConfig.fontFamily,
        color: "text.main",
        transform: "uppercase",
    },
    headerSecondary: {
        forwardAs: "p",
        size: "15px",
        fontFamily: bpFontConfig.fontFamily,
        transform: "uppercase",
        weight: bpFontConfig.weight.bold,
    },
    headerTertiary: {
        forwardAs: "p",
        size: "15px",
        fontFamily: bpFontConfig.fontFamily,
        color: "text.main",
        transform: "uppercase",
    },
    legend: {
        forwardAs: "span",
        size: "10px",
        transform: "uppercase",
        weight: bpFontConfig.weight.bold,
    },
} as xBaseTheme["typography"];
